import { useState } from "react";
import type { Project } from "../data/portfolio";
import { summaries } from "../data/summaries";

type ProjectCardProps = {
  project: Project;
};

const toEmbedUrl = (url: string) => {
  const youtuShort = /https?:\/\/youtu\.be\/([^?]+)/;
  const youtuWatch = /https?:\/\/(www\.)?youtube\.com\/watch\?v=([^&]+)/;
  if (youtuShort.test(url)) {
    const id = url.match(youtuShort)?.[1];
    return id ? `https://www.youtube.com/embed/${id}` : url;
  }
  if (youtuWatch.test(url)) {
    const id = url.match(youtuWatch)?.[2];
    return id ? `https://www.youtube.com/embed/${id}` : url;
  }
  return url;
};

const ProjectCard = ({ project }: ProjectCardProps) => {
  const summary = summaries[project.summaryKey] ?? "";
  const [expanded, setExpanded] = useState(false);
  const limit = 220;
  const isLong = summary.length > limit;
  const displaySummary = isLong && !expanded ? `${summary.slice(0, limit).trimEnd()}…` : summary;

  return (
    <article className="project-card">
      <div className="card-body">
        <div className="card-head">
          <p className="eyebrow">{project.timeline}</p>
          <h3>{project.title}</h3>
          <p className="card-summary">{displaySummary}</p>
          {isLong && (
            <button className="read-toggle" type="button" onClick={() => setExpanded((v) => !v)}>
              {expanded ? "Show less" : "Read more"}
            </button>
          )}
        </div>
        <div className="tech-list">
          {project.technologies.map((tech) => (
            <span key={tech} className="pill subtle">
              {tech}
            </span>
          ))}
        </div>
        {project.media && (
          <div className="media-gallery">
            {project.media.map((media, idx) =>
              media.type === "image" ? (
                <figure key={media.src + idx} className="media-card">
                  <img src={media.src} alt={media.caption ?? `${project.title} media ${idx + 1}`} loading="lazy" />
                  {media.caption && <figcaption>{media.caption}</figcaption>}
                </figure>
              ) : (
                <div key={media.src + idx} className="media-card video-card">
                  <div className="video-wrapper">
                    <iframe
                      src={toEmbedUrl(media.src)}
                      title={media.caption ?? project.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  {media.caption && <p className="meta">{media.caption}</p>}
                </div>
              )
            )}
          </div>
        )}

        <div className="tag-row">
          <div className="tag-group">
            <p className="meta">Technologies</p>
            <div className="chip-row">
              {project.tags.languages.map((lang) => (
                <span key={lang} className="pill subtle">
                  {lang}
                </span>
              ))}
            </div>
          </div>
          <div className="tag-group">
            <p className="meta">Categories</p>
            <div className="chip-row">
              {project.tags.categories.map((cat) => (
                <span key={cat} className="pill subtle">
                  {cat}
                </span>
              ))}
            </div>
          </div>
          <div className="tag-group">
            <p className="meta">Context</p>
            <span className="pill subtle">{project.tags.context}</span>
          </div>
        </div>
      </div>
    </article>
  );
};

export default ProjectCard;
